// The review family's knowledge/backlog PIPELINE kinds (kb ingest + backlog
// import + scout mint) — split out of `review.ts` purely for size (biome's
// 800-line file cap; the family itself is one table, see `review.ts`'s own
// composition and its `TERMINAL_RENDERER_GROUPS` entry in `project.ts`). NOT
// every `kb.*` kind: `kb.citations_uncredited` is the KB gardener's own
// usage-credit bookkeeping, a different concern, and stays in `review.ts`'s own
// portion — the disjointness test in `review.test.ts` catches an accidental
// duplicate either way, so a future `kb.*` addition can go in either file
// without silently colliding. Shared helpers live in `./review-payload`, a leaf
// both this file and `review.ts` import from — see that file's header for why
// NOT `review.ts`.

import type { TerminalRenderContext, TerminalRendered } from '../../../wire/terminal';
import { terminalRendererGroup } from '../../../wire/terminal';
import { ACCOUNT_VERB_OK } from './account-verb';
import { numField, rec, refsFor, repoSubject, strOpt, strOr } from './review-payload';

// ─── kb.page_upserted (one kind, two visibilities) ───────────────────────────
//
// The worker's two writers (`consumers/kb-import.ts`'s seeding and the review
// write-back, `wiring/kb-page-audit.ts`) stamp `visibleToMaintainer: false`:
// internal mechanics, nothing an operator acts on, so their rows render nothing. The
// api's REST upsert (`apps/api/src/routes/kb.ts`, #6124) stamps `true`: a person or a
// token REPLACED a page, which showed on this console as a tool-call row before the
// kind was shared, and renders as the account verb it was (`./account-verb.ts`).
// Never `'omit'`: an omitted kind is dropped from `TERMINAL_KINDS`
// (`apps/api/src/routes/terminal.ts`), so the REST row would never be fetched at all.

function kbPageUpserted(ctx: TerminalRenderContext): TerminalRendered | null {
  return ctx.row.visibleToMaintainer ? ACCOUNT_VERB_OK.render(ctx) : null;
}

// ─── kb.page_capped (`visibleToMaintainer: true`) ────────────────────────────
//
// `apps/worker/src/wiring/kb-page-audit.ts` writes `input: { runId, taskId, repoId,
// repo, slug, event, prNumber, swap }`, `output: { pages, maxPages, remedy }`.

function kbPageCapped(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.kb.page_capped',
      params: {
        repo: repo ?? 'unknown repo',
        slug: strOr(input['slug'], 'unknown'),
        pages: numField(output['pages'], 0),
        maxPages: numField(output['maxPages'], 0),
      },
    },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, taskId: strOpt(input['taskId']), sessionId: ctx.row.sessionId }),
  };
}

// ─── backlog.imported / backlog.import_skipped (`visibleToMaintainer: true`) ─
//
// Checked the writer despite the task brief's "reasonable omit candidate" hint
// (rule 3): `backlog-import.ts`'s `settle` sets `visibleToMaintainer: true` on
// BOTH kinds and its own comment says why — "the maintainer's own tracker was
// read and their queue moved; that is theirs to see". Rendered, not omitted.

function backlogImported(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.backlog.imported',
      params: {
        repo: repo ?? 'unknown repo',
        filed: numField(output['filed'], 0),
        listed: numField(output['listed'], 0),
      },
    },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

function backlogImportSkipped(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.backlog.import_skipped',
      params: { repo: repo ?? 'unknown repo', reason: strOr(output['reason'], 'unknown') },
    },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

/**
 * This sub-table's 8 entries, spread into `review.ts`'s composed export. Kept
 * SEPARATE from `TERMINAL_RENDERERS_REVIEW` (rather than exported already-merged)
 * so a partition test can prove it and `review.ts`'s own portion are disjoint —
 * see `review.test.ts`.
 */
export const TERMINAL_RENDERERS_REVIEW_KNOWLEDGE = terminalRendererGroup({
  'kb.page_upserted': { level: 'ok', actor: 'system', render: kbPageUpserted },
  'kb.page_capped': {
    level: 'warn',
    actor: 'system',
    render: kbPageCapped,
  },
  // Internal no-op/dedup bookkeeping (`visibleToMaintainer: false` on every
  // call site) — the reasons enumerated (`already_seeded`, `duplicate_slugs`,
  // …) are producer-side housekeeping, not something an operator acts on.
  'kb.import.skipped': 'omit',
  // Internal model/schema failure inside a retried background import
  // (`visibleToMaintainer: false`) — no maintainer action, the retry lane owns
  // recovery.
  'kb.import.failed': 'omit',
  'backlog.imported': {
    level: 'ok',
    actor: 'system',
    render: backlogImported,
  },
  'backlog.import_skipped': {
    level: 'info',
    actor: 'system',
    render: backlogImportSkipped,
  },
  // Autonomous scout mint, `visibleToMaintainer: false` — internal pipeline
  // bookkeeping ("a planning task was minted"), not a line an operator reads
  // for signal.
  'scout_backlog.drained': 'omit',
  // Its decline twin (#6370), same bookkeeping posture.
  'scout_backlog.declined': 'omit',
  // Same shape as the scout drain, same reason: `visibleToMaintainer: false`,
  // internal cron mint.
  'repo_survey.pumped': 'omit',
});
